import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { 
  Loader2, FileText, Layers, Download, Upload, Eye, Search, Calendar, 
  User, DollarSign, Clock, ThumbsUp, ThumbsDown, MessageSquare, RefreshCw, Building,
  LayoutGrid, GalleryHorizontalEnd, AlertCircle, CheckCircle2, Trash2, Coins, UploadCloud, History
} from "lucide-react";
import { format } from "date-fns";
import { exportCsv } from "@/utils/csv";

const fmtDate = (d) => { try { return format(new Date(d), "MMM d, yyyy h:mm a"); } catch { return d || "-"; } };
const fmtCost = (cost) => {
  if (!cost) return "-";
  if (cost.cost_usd !== undefined) return `$${Number(cost.cost_usd).toFixed(4)}`;
  const total = (cost.input_cost || 0) + (cost.output_cost || 0);
  return total > 0 ? `$${total.toFixed(4)}` : "-";
};
// Prefer the cumulative figure (current run + all retries + per-slide
// cost on decks) when present — that's the *true* spend the admin cares
// about. Fall back to the single-attempt cost dict if cumulative wasn't
// computed for some reason (very old rows).
const fmtCumulativeCost = (log) => {
  const total = log.total_cost_usd;
  if (total !== undefined && total !== null) {
    const tries = log.attempt_count || 1;
    const slides = log.type === "deck" ? (log.num_slides || (log.slide_ids || []).length || 0) : 0;
    const subtitle = slides > 1
      ? `${tries} attempt${tries > 1 ? "s" : ""} · ${slides} slides`
      : `${tries} attempt${tries > 1 ? "s" : ""}`;
    return { value: `$${Number(total).toFixed(4)}`, subtitle };
  }
  return { value: fmtCost(log.cost), subtitle: null };
};
const fmtDuration = (s) => {
  if (!s) return "-";
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
};

const StatusDot = ({ status }) => {
  if (status === "complete") return <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />;
  if (status === "failed") return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
  return <Loader2 className="w-3.5 h-3.5 text-amber-500 animate-spin" />;
};

export default function AdminLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  // location.state snapshot from User Management → keeps the deep-link
  // banner rendering even if the user has zero log entries (in which case
  // we'd otherwise have no log row to derive name/email from).
  const navState = location.state || {};
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [reuploadLog, setReuploadLog] = useState(null);
  const [reuploadNotes, setReuploadNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ─── "Upload new" + Version history (admin-only) ────────────────────
  // Upload-new: lets admins seed a brand-new slide/deck row from a PPTX
  // (e.g. when recreating a historically generated file or attributing a
  // hand-crafted deck to a user).
  // Version history: lists every prior incarnation of a row's PPTX so
  // admins can roll back a bad re-upload without manually restoring
  // GridFS bytes.
  const [showUploadNew, setShowUploadNew] = useState(false);
  const [uploadNewFile, setUploadNewFile] = useState(null);
  const [uploadNewTitle, setUploadNewTitle] = useState("");
  const [uploadNewUserId, setUploadNewUserId] = useState("");
  const [uploadNewNotes, setUploadNewNotes] = useState("");
  const [versionLog, setVersionLog] = useState(null);
  const [versionData, setVersionData] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(false);
  
  // Pagination state
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const PAGE_SIZE = 20;
  
  // Additional filters — initialise from URL query params so admins can
  // deep-link to a single user's logs (e.g. from User Management → "View
  // logs" per-row action). `?user=Name` and `?user_id=<uuid>` both work.
  const [filterUser, setFilterUser] = useState(searchParams.get("user") || "");
  const filterUserId = searchParams.get("user_id") || "";
  const [filterOrg, setFilterOrg] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Unique users and orgs for filter dropdowns
  const uniqueUsers = [...new Set(logs.map(l => l.user?.name).filter(Boolean))].sort();
  const uniqueOrgs = [...new Set(logs.map(l => l.user?.organization).filter(Boolean))].sort();

  const fetchLogs = async (loadMore = false, reset = false) => {
    try {
      const skip = loadMore && !reset ? logs.length : 0;
      const res = await axios.get(`${API}/admin/logs?limit=${PAGE_SIZE}&skip=${skip}`, { withCredentials: true });
      
      if (loadMore && !reset) {
        setLogs(prev => [...prev, ...res.data.items]);
      } else {
        setLogs(res.data.items);
      }
      setTotal(res.data.total);
      setHasMore(res.data.has_more);
    } catch {
      toast.error("Failed to load logs");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    await fetchLogs(true);
  };
  
  const handleRefresh = () => {
    setLoading(true);
    fetchLogs(false, true);
    toast.success("Logs refreshed");
  };

  useEffect(() => { fetchLogs(); }, []);

  const handleDelete = async (log) => {
    if (!window.confirm(`Delete this ${log.type}? This action cannot be undone.`)) return;
    setDeletingId(log.id);
    try {
      const endpoint = log.type === "deck" ? `${API}/decks/${log.id}` : `${API}/presentations/${log.id}`;
      await axios.delete(endpoint, { withCredentials: true });
      toast.success(`${log.type === "deck" ? "Deck" : "Presentation"} deleted`);
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const handleReupload = async () => {
    if (!reuploadLog || !selectedFile) { toast.error("Please select a file"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      if (reuploadNotes) formData.append("notes", reuploadNotes);
      await axios.post(`${API}/admin/logs/${reuploadLog.type}/${reuploadLog.id}/reupload`, formData, { withCredentials: true });
      toast.success("File reuploaded successfully");
      setReuploadLog(null); setSelectedFile(null); setReuploadNotes("");
      fetchLogs();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to reupload"); }
    finally { setUploading(false); }
  };

  const handleUploadNew = async () => {
    if (!uploadNewFile) { toast.error("Please select a .pptx file"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadNewFile);
      if (uploadNewTitle) formData.append("title", uploadNewTitle);
      if (uploadNewUserId) formData.append("user_id", uploadNewUserId);
      if (uploadNewNotes) formData.append("notes", uploadNewNotes);
      const res = await axios.post(`${API}/admin/upload-new`, formData, { withCredentials: true });
      toast.success(`Created ${res.data.type} with ${res.data.slide_count} slide${res.data.slide_count === 1 ? "" : "s"}`);
      setShowUploadNew(false);
      setUploadNewFile(null); setUploadNewTitle(""); setUploadNewUserId(""); setUploadNewNotes("");
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const openVersionHistory = async (log) => {
    setVersionLog(log);
    setLoadingVersions(true);
    try {
      const res = await axios.get(`${API}/admin/logs/${log.type}/${log.id}/versions`, { withCredentials: true });
      setVersionData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load version history");
      setVersionLog(null);
    } finally {
      setLoadingVersions(false);
    }
  };

  const downloadVersion = (entry) => {
    if (!entry?.download_url) {
      toast.error("This version's bytes are no longer available");
      return;
    }
    // `download_url` from the backend is path-prefixed with /api (it was
    // built without knowing whether the caller's `API` constant already
    // includes the prefix). API has /api → strip it from the entry path.
    const path = entry.download_url.replace(/^\/api/, "");
    const a = document.createElement("a");
    a.href = `${API}${path}`;
    a.rel = "noopener";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleDownload = (log) => {
    // Let the server's Content-Disposition decide the filename
    // (slugified <title>.pptx via routes/presentations.py).
    const a = document.createElement("a");
    a.href = log.type === "deck" ? `${API}/download-deck/${log.id}` : `${API}/download/${log.id}`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const filtered = logs.filter(log => {
    const matchSearch = log.topic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.user?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.user?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchType = filterType === "all" || log.type === filterType || 
      (filterType === "failed" && (log.status === "failed" || log.status === "generating"));
    const matchUser = !filterUser || log.user?.name === filterUser;
    // Admin-deep-link path: filter by exact user_id when ?user_id=<uuid> is present.
    const matchUserId = !filterUserId || log.user?.user_id === filterUserId;
    const matchOrg = !filterOrg || log.user?.organization === filterOrg;
    const matchStatus = filterStatus === "all" || log.status === filterStatus;
    return matchSearch && matchType && matchUser && matchUserId && matchOrg && matchStatus;
  });

  // Use the server-computed `total_cost_usd` (current + retries + child
  // slides for decks). Falls back to the per-attempt cost dict for old
  // rows that haven't been re-fetched since the cumulative-rollup shipped.
  const totalCost = logs.reduce(
    (sum, l) => sum + (l.total_cost_usd ?? l.cost?.cost_usd ?? 0),
    0,
  );

  // CSV export — covers the visible filtered rows. Matches the Logs table
  // columns + a few admin-only fields (cost / time-taken / user email)
  // that don't fit on the card view but matter for offline analysis.
  const handleExportCsv = () => {
    const headers = [
      "ID", "Type", "Status", "Topic", "User", "Email", "Organization",
      "Engine", "Cost USD (this run)", "Cumulative Cost USD", "Attempts",
      "Credits charged", "Time taken (s)", "Feedback", "Created at",
    ];
    const rows = [headers, ...filtered.map(log => [
      log.id || "",
      log.type || "",
      log.status || "",
      log.topic || "",
      log.user?.name || "",
      log.user?.email || "",
      log.user?.organization || "",
      log.engine || "",
      log.cost?.cost_usd != null ? Number(log.cost.cost_usd).toFixed(4) : "",
      log.total_cost_usd != null ? Number(log.total_cost_usd).toFixed(4) : "",
      log.attempt_count ?? "",
      log.credits_charged ?? "",
      log.time_taken != null ? Math.round(log.time_taken) : "",
      log.feedback?.rating || "",
      log.created_at || "",
    ])];
    const stamp = new Date().toISOString().split("T")[0];
    exportCsv(`wokelo-logs-${stamp}.csv`, rows);
    toast.success(`Exported ${filtered.length} log rows`);
  };

  // When deep-linking from User Management with ?user_id=<uuid>, surface
  // the target user's name/email above the filters as a clear banner.
  // Prefer the state snapshot passed by the parent navigation; fall back to
  // the first matching log row (handles direct-URL paste case). This keeps
  // the banner usable even when the user has zero log entries.
  const deepLinkUser = filterUserId
    ? (navState.user && navState.user.user_id === filterUserId
        ? navState.user
        : (logs.find(l => l.user?.user_id === filterUserId)?.user || null))
    : null;

  if (loading) {
    return <div className="page-container flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="page-container" data-testid="admin-logs-page">
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
        <span>Admin</span><span>/</span><span className="text-gray-900">Logs</span>
        {filterUserId && (
          <>
            <span>/</span>
            <Link to="/admin/users" className="hover:text-gray-600">Users</Link>
            <span>/</span>
            <span className="text-gray-900">{deepLinkUser?.name || deepLinkUser?.email || "User"}</span>
          </>
        )}
      </div>

      {filterUserId && deepLinkUser && (
        <div
          className="flex items-center justify-between gap-3 mb-5 rounded-md border border-teal-200 bg-teal-50 px-4 py-3"
          data-testid="user-deep-link-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <User className="w-4 h-4 text-teal-700 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-teal-900 truncate">
                Showing logs for {deepLinkUser.name || deepLinkUser.email}
              </p>
              {deepLinkUser.email && deepLinkUser.name && (
                <p className="text-xs text-teal-700 truncate">{deepLinkUser.email}</p>
              )}
            </div>
          </div>
          <button
            onClick={() => setSearchParams({})}
            className="text-xs font-semibold text-teal-700 hover:text-teal-900 flex-shrink-0"
            data-testid="clear-user-filter"
          >
            Show all users
          </button>
        </div>
      )}

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope']">Logs</h1>
          <p className="text-sm text-gray-500 mt-1">
            {logs.length} total runs
            {totalCost > 0 && <> &middot; ${totalCost.toFixed(2)} total cost</>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowUploadNew(true)}
            data-testid="logs-upload-new-btn"
          >
            <UploadCloud className="w-3.5 h-3.5 mr-1" />Upload new
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            disabled={!filtered.length}
            data-testid="logs-export-csv-btn"
          >
            <Download className="w-3.5 h-3.5 mr-1" />Export CSV
          </Button>
          <div className="flex bg-gray-100 rounded-md p-0.5">
            <button onClick={() => setViewMode("grid")}
              className={`p-1.5 rounded ${viewMode === "grid" ? "bg-white shadow-sm" : "text-gray-400"}`}
              data-testid="toggle-grid-logs">
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button onClick={() => setViewMode("gallery")}
              className={`p-1.5 rounded ${viewMode === "gallery" ? "bg-white shadow-sm" : "text-gray-400"}`}
              data-testid="toggle-gallery-logs">
              <GalleryHorizontalEnd className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 mb-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search by topic, user..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="pl-9" data-testid="log-search" />
          </div>
          <div className="flex gap-1">
            {[{ key: "all", label: "All" }, { key: "slide", label: "Slides" }, { key: "deck", label: "Decks" }, { key: "failed", label: "Failed/Queued" }].map(f => (
              <button key={f.key} onClick={() => setFilterType(f.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  filterType === f.key ? "bg-black text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`} data-testid={`filter-${f.key}`}>{f.label}</button>
            ))}
          </div>
        </div>
        
        {/* Additional Filters */}
        <div className="flex flex-wrap gap-2">
          <select 
            value={filterUser} 
            onChange={e => setFilterUser(e.target.value)}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-black"
            data-testid="filter-user"
          >
            <option value="">All Users</option>
            {uniqueUsers.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          
          <select 
            value={filterOrg} 
            onChange={e => setFilterOrg(e.target.value)}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-black"
            data-testid="filter-org"
          >
            <option value="">All Organizations</option>
            {uniqueOrgs.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          
          <select 
            value={filterStatus} 
            onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-black"
            data-testid="filter-status"
          >
            <option value="all">All Statuses</option>
            <option value="complete">Complete</option>
            <option value="generating">Generating</option>
            <option value="failed">Failed</option>
          </select>
          
          {(filterUser || filterUserId || filterOrg || filterStatus !== "all") && (
            <button 
              onClick={() => {
                setFilterUser("");
                setFilterOrg("");
                setFilterStatus("all");
                // Clear deep-link filters too so the URL no longer scopes
                // to a specific user.
                if (filterUserId || searchParams.get("user")) {
                  setSearchParams({});
                }
              }}
              className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {viewMode === "gallery" ? (
        /* Gallery Card View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(log => (
            <Card key={`${log.type}-${log.id}`} className="border-gray-200 shadow-none hover:shadow-md transition-all overflow-hidden"
              data-testid={`log-card-${log.id}`}>
              <div className={`aspect-video relative flex items-center justify-center ${
                log.status === "generating" ? "bg-gradient-to-br from-amber-50 to-amber-100" :
                log.status === "failed" ? "bg-gradient-to-br from-red-50 to-red-100" :
                "bg-gradient-to-br from-gray-100 to-gray-200"
              }`}>
                {log.status === "complete" && (
                  <img src={`${API}/thumbnails/${log.type === "deck" && log.slide_ids?.length > 0 ? log.slide_ids[0] : log.id}`} alt="Preview" className="w-full h-full object-cover" loading="lazy"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                )}
                <div className="absolute top-2 left-2">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 ${
                    log.type === "deck" ? "text-indigo-700 bg-indigo-50/90 border border-indigo-200" : "text-gray-500 bg-white/90 border border-gray-200"
                  }`}>
                    {log.type === "deck" ? <><Layers className="w-2.5 h-2.5" />Deck</> : <><FileText className="w-2.5 h-2.5" />Slide</>}
                  </span>
                </div>
                {(log.research_provider || log.inputs?.research_text) && (
                  <div className="absolute top-2 right-2">
                    <span
                      className={`inline-flex items-center text-[10px] font-semibold rounded-full px-2 py-0.5 border ${(log.research_provider || "").toLowerCase().startsWith("premium")
                        ? "bg-amber-50/90 text-amber-800 border-amber-200"
                        : "bg-teal-50/90 text-teal-800 border-teal-200"}`}
                    >
                      {(log.research_provider || "").toLowerCase().startsWith("premium") ? "Premium" : "Public"}
                    </span>
                  </div>
                )}
              </div>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900 line-clamp-1 mb-1 flex-1">{log.topic || "Untitled"}</p>
                  {log.deleted_at && (
                    <span
                      className="flex-shrink-0 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 rounded-full px-1.5 py-0.5"
                      title={`User-deleted on ${new Date(log.deleted_at).toLocaleString()}`}
                      data-testid={`gallery-deleted-${log.id}`}
                    >
                      Deleted
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-[10px] text-gray-400 mb-1">
                  <StatusDot status={log.status} />
                  <span>{fmtDate(log.created_at)}</span>
                  <span className="text-gray-600 font-mono" data-testid={`gallery-log-id-${log.id}`}>ID: {log.id?.slice(0, 12)}</span>
                </div>
                {log.user && (
                  <div className="flex items-center gap-1 text-[10px] text-gray-500 mb-1">
                    <User className="w-2.5 h-2.5" />{log.user.name} ({log.user.email})
                    {log.user.organization && <span className="text-gray-300">| {log.user.organization}</span>}
                  </div>
                )}
                <div className="flex items-center gap-3 text-[10px] text-gray-400 mb-2">
                  <span
                    className="flex items-center gap-0.5"
                    title={
                      log.attempt_count > 1
                        ? `Cumulative across ${log.attempt_count} attempts (this run + retries)${log.type === "deck" ? " + per-slide costs" : ""}`
                        : "Total cost"
                    }
                    data-testid={`log-grid-cost-${log.id}`}
                  >
                    <DollarSign className="w-2.5 h-2.5" />{fmtCumulativeCost(log).value}
                    {log.attempt_count > 1 && (
                      <span className="ml-0.5 text-gray-500">×{log.attempt_count}</span>
                    )}
                  </span>
                  <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{log.engine || "advanced"}</span>
                  {log.credits_charged != null && (
                    <span className="flex items-center gap-0.5 text-teal-600" data-testid={`log-grid-credits-${log.id}`}>
                      <Coins className="w-2.5 h-2.5" />{log.credits_charged} credits
                    </span>
                  )}
                  {(log.feedback?.rating === "up" || log.feedback?.rating === "like") && <ThumbsUp className="w-3 h-3 text-green-500" />}
                  {(log.feedback?.rating === "down" || log.feedback?.rating === "dislike") && <ThumbsDown className="w-3 h-3 text-red-500" />}
                </div>
                {log.feedback?.feedback_text && (
                  <p className="text-[10px] text-gray-400 italic truncate mb-2" title={log.feedback.feedback_text}>
                    "{log.feedback.feedback_text}"
                  </p>
                )}
                <div className="flex gap-1">
                  <Link to={`/admin/details/${log.type}/${log.id}`} className="flex-1">
                    <Button size="sm" variant="outline" className="w-full text-xs h-7"><Eye className="w-3 h-3 mr-1" />View</Button>
                  </Link>
                  {log.status === "complete" && (
                    <>
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleDownload(log)}><Download className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => { setReuploadLog(log); setSelectedFile(null); setReuploadNotes(""); }} title="Reupload"><Upload className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => openVersionHistory(log)} title="Version history" data-testid={`versions-${log.id}`}><History className="w-3 h-3" /></Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          
          {filtered.length === 0 && (
            <div className="col-span-full py-12 text-center text-sm text-gray-400">
              {filterUserId ? "No generation logs for this user yet" :
                (searchQuery || filterUser || filterOrg || filterStatus !== "all"
                  ? "No logs found matching your filters"
                  : "No generation logs yet")}
            </div>
          )}
          
          {/* Load More Button for Gallery View */}
          {hasMore && !searchQuery && !filterUser && !filterOrg && filterStatus === "all" && (
            <div className="col-span-full flex justify-center py-6">
              <Button 
                variant="outline" 
                onClick={handleLoadMore} 
                disabled={loadingMore}
                className="min-w-[200px]"
                data-testid="load-more-gallery-btn"
              >
                {loadingMore ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading...</>
                ) : (
                  <>Load More ({total - logs.length} remaining)</>
                )}
              </Button>
            </div>
          )}
        </div>
      ) : (
        /* Table/Grid View */
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="logs-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Type</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Title</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">User</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Org</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Approach</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Status</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Engine</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Cost (cumulative)</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Credits</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Time</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Feedback</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Date</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(log => (
                  <tr key={`${log.type}-${log.id}`} className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                    data-testid={`log-row-${log.id}`}>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap ${
                        log.type === "deck" ? "text-indigo-700 bg-indigo-50 border border-indigo-200" : "text-gray-500 bg-gray-100 border border-gray-200"
                      }`}>
                        {log.type === "deck" ? <><Layers className="w-2.5 h-2.5 flex-shrink-0" /><span>Deck{log.num_slides ? ` (${log.num_slides})` : ""}</span></> : <><FileText className="w-2.5 h-2.5 flex-shrink-0" /><span>Slide</span></>}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 max-w-[200px]">
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs font-medium text-gray-900 truncate">{log.topic || "Untitled"}</p>
                        {log.deleted_at && (
                          <span
                            className="flex-shrink-0 inline-flex items-center text-[9px] font-bold uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 rounded px-1 py-0.5"
                            title={`User-deleted on ${new Date(log.deleted_at).toLocaleString()}`}
                            data-testid={`row-deleted-${log.id}`}
                          >
                            Deleted
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-600 font-mono" data-testid={`log-id-${log.id}`}>ID: {log.id}</p>
                      {log.admin_notes && <p className="text-[10px] text-blue-500">Admin edited</p>}
                    </td>
                    <td className="px-4 py-2.5">
                      {log.user ? (
                        <div>
                          <p className="text-xs text-gray-700">{log.user.name}</p>
                          <p className="text-[10px] text-gray-400">{log.user.email}</p>
                        </div>
                      ) : <span className="text-xs text-gray-300">-</span>}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">{log.user?.organization || "-"}</td>
                    <td className="px-4 py-2.5">
                      {(log.research_provider || log.inputs?.research_text) ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[10px] font-semibold text-violet-700">Wokelo research</span>
                          <span
                            className={`inline-flex items-center text-[10px] font-semibold rounded-full px-1.5 py-0.5 border w-fit ${(log.research_provider || "").toLowerCase().startsWith("premium")
                              ? "bg-amber-50 text-amber-800 border-amber-200"
                              : "bg-teal-50 text-teal-800 border-teal-200"}`}
                            data-testid={`log-data-tier-${log.id}`}
                          >
                            {(log.research_provider || "").toLowerCase().startsWith("premium") ? "Premium" : "Public"}
                          </span>
                        </div>
                      ) : (
                        <span className="text-[10px] font-semibold text-gray-500">Only my content</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5"><StatusDot status={log.status} /></td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">{log.engine || "advanced"}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-700" data-testid={`log-cost-${log.id}`}>
                      {(() => {
                        const c = fmtCumulativeCost(log);
                        return (
                          <div title={
                            log.attempt_count > 1
                              ? `Cumulative across ${log.attempt_count} attempts (this run + retries)${log.type === "deck" ? " + per-slide costs" : ""}`
                              : "Total cost"
                          }>
                            <div className="font-medium">{c.value}</div>
                            {c.subtitle && <div className="text-[10px] text-gray-400 leading-tight">{c.subtitle}</div>}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-700 font-medium" data-testid={`log-credits-${log.id}`}>
                      {log.credits_charged != null ? log.credits_charged : <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap" data-testid={`log-time-${log.id}`}>
                      {log.time_taken != null
                        ? (log.time_taken >= 60 ? `${Math.round(log.time_taken / 60)}m` : `${Math.round(log.time_taken)}s`)
                        : <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        {(log.feedback?.rating === "up" || log.feedback?.rating === "like") && <ThumbsUp className="w-3.5 h-3.5 text-green-500" />}
                        {(log.feedback?.rating === "down" || log.feedback?.rating === "dislike") && <ThumbsDown className="w-3.5 h-3.5 text-red-500" />}
                        {log.feedback?.feedback_text && (
                          <span className="text-[10px] text-gray-400 truncate max-w-[80px]" title={log.feedback.feedback_text}>
                            <MessageSquare className="w-3 h-3 inline mr-0.5" />
                          </span>
                        )}
                        {!log.feedback?.rating && <span className="text-xs text-gray-300">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtDate(log.created_at)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-1">
                        <Link to={`/admin/details/${log.type}/${log.id}`}>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0"><Eye className="w-3.5 h-3.5" /></Button>
                        </Link>
                        {log.status === "complete" && (
                          <>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDownload(log)}>
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setReuploadLog(log); setSelectedFile(null); setReuploadNotes(""); }} title="Reupload">
                              <Upload className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openVersionHistory(log)} title="Version history">
                              <History className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" 
                          onClick={() => handleDelete(log)}
                          disabled={deletingId === log.id}
                          title="Delete"
                        >
                          {deletingId === log.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="py-12 text-center text-sm text-gray-400">
              {searchQuery ? "No logs found matching your search" : "No generation logs yet"}
            </div>
          )}
          
          {/* Load More Button */}
          {hasMore && !searchQuery && (
            <div className="flex justify-center py-6">
              <Button 
                variant="outline" 
                onClick={handleLoadMore} 
                disabled={loadingMore}
                className="min-w-[200px]"
                data-testid="load-more-logs-btn"
              >
                {loadingMore ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading...</>
                ) : (
                  <>Load More ({total - logs.length} remaining)</>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Reupload Dialog */}
      <Dialog open={!!reuploadLog} onOpenChange={() => setReuploadLog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reupload Fixed {reuploadLog?.type === "deck" ? "Deck" : "Slide"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm font-medium text-gray-700">{reuploadLog?.topic || "Untitled"}</p>
              <p className="text-xs text-gray-400">ID: {reuploadLog?.id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Upload Fixed PPTX</label>
              <input ref={fileInputRef} type="file" accept=".pptx" onChange={e => setSelectedFile(e.target.files?.[0] || null)} className="hidden" />
              <button onClick={() => fileInputRef.current?.click()}
                className="w-full p-4 border-2 border-dashed border-gray-200 rounded-lg text-center hover:border-gray-400 transition-colors">
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-700"><FileText className="w-4 h-4" />{selectedFile.name}</div>
                ) : (
                  <div className="text-sm text-gray-500"><Upload className="w-5 h-5 mx-auto mb-1" />Click to select a PPTX file</div>
                )}
              </button>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Admin Notes (Optional)</label>
              <Textarea placeholder="Notes about what was fixed..." value={reuploadNotes} onChange={e => setReuploadNotes(e.target.value)} className="min-h-[80px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReuploadLog(null)}>Cancel</Button>
            <Button onClick={handleReupload} disabled={uploading || !selectedFile} className="bg-black hover:bg-gray-800 text-white">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload-new (creates a brand-new slide/deck row) */}
      <Dialog open={showUploadNew} onOpenChange={(o) => !o && setShowUploadNew(false)}>
        <DialogContent className="max-w-md" data-testid="upload-new-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UploadCloud className="w-4 h-4" />Upload new presentation
            </DialogTitle>
            <DialogDescription className="text-xs">
              Create a brand-new slide or deck row from a PPTX. Multi-slide files
              are auto-split into per-slide thumbnails so the gallery carousel works.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1.5 block">PPTX file</label>
              <Input
                type="file" accept=".pptx"
                onChange={e => setUploadNewFile(e.target.files?.[0] || null)}
                data-testid="upload-new-file"
              />
              {uploadNewFile && (
                <p className="text-[11px] text-gray-400 mt-1">
                  {uploadNewFile.name} · {(uploadNewFile.size / 1024 / 1024).toFixed(1)} MB
                </p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1.5 block">Title (optional)</label>
              <Input
                placeholder="Defaults to filename"
                value={uploadNewTitle}
                onChange={e => setUploadNewTitle(e.target.value)}
                data-testid="upload-new-title"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1.5 block">Attribute to user (optional)</label>
              <Input
                placeholder="user email — leave blank for your admin account"
                value={uploadNewUserId}
                onChange={e => setUploadNewUserId(e.target.value)}
                data-testid="upload-new-user-id"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Email or user_id. The deck will show up in this user's History.
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1.5 block">Notes (optional)</label>
              <Textarea
                placeholder="Why was this uploaded? (e.g. recreated lost deck, demo content...)"
                value={uploadNewNotes}
                onChange={e => setUploadNewNotes(e.target.value)}
                className="min-h-[60px]"
                data-testid="upload-new-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadNew(false)}>Cancel</Button>
            <Button
              onClick={handleUploadNew}
              disabled={uploading || !uploadNewFile}
              className="bg-black hover:bg-gray-800 text-white"
              data-testid="upload-new-submit"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version history */}
      <Dialog open={!!versionLog} onOpenChange={(o) => !o && setVersionLog(null)}>
        <DialogContent className="max-w-xl" data-testid="version-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-4 h-4" />Version history
            </DialogTitle>
            <DialogDescription className="text-xs">
              Every previous version of <span className="font-mono text-gray-700">{versionLog?.topic || versionLog?.title || versionLog?.id}</span>.
              Files are kept forever — users only ever see the current version.
            </DialogDescription>
          </DialogHeader>
          {loadingVersions ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : versionData ? (
            <div className="space-y-2 pt-1 max-h-[420px] overflow-y-auto">
              <div className="text-xs text-gray-500 px-2">
                Current version: <span className="font-semibold text-gray-900">v{versionData.current_version}</span>
                {versionData.last_reuploaded_at && (
                  <> · last replaced {new Date(versionData.last_reuploaded_at).toLocaleString()}</>
                )}
              </div>
              {(versionData.history || []).length === 0 ? (
                <div className="px-2 py-6 text-center text-xs text-gray-400 border border-dashed border-gray-200 rounded-md">
                  No previous versions — this is the original file.
                </div>
              ) : (
                versionData.history.map((entry, idx) => (
                  <div
                    key={entry.version || idx}
                    className="flex items-start gap-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50"
                    data-testid={`version-entry-${entry.version}`}
                  >
                    <div className="flex-shrink-0 w-12 text-center">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">v{entry.version}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-700">
                        Replaced <strong>{entry.replaced_at ? new Date(entry.replaced_at).toLocaleString() : "—"}</strong>
                      </p>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        By <span className="font-mono">{entry.replaced_by || "—"}</span> · {entry.source}
                      </p>
                      {entry.reason && (
                        <p className="text-xs text-gray-600 mt-1 italic">"{entry.reason}"</p>
                      )}
                    </div>
                    <Button
                      size="sm" variant="outline"
                      className="text-xs h-7"
                      onClick={() => downloadVersion(entry)}
                      disabled={!entry.download_url}
                      data-testid={`download-version-${entry.version}`}
                    >
                      <Download className="w-3 h-3 mr-1" />Download
                    </Button>
                  </div>
                ))
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVersionLog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
